import React from "react";
import { IsDate, Required, TractieForm } from "@tractie/components";
import { combineDateAndTime } from "@shared/common/utils/luxonHelpers";
import { useLocationListQuery } from "./useLocationListQuery";
import { useCreateInstanceMutation } from "./useCreateInstanceMutation";
import { DateTime } from "luxon";

interface MpsLoadingProps {
  readonly closeDialog: () => void;
}

interface LoadPlanForm {
  hub: string;
  startDate: string;
  startTime: `${number}${number}:${number}${number}`;
  endDate: string;
  endTime: `${number}${number}:${number}${number}`;
}

export function MpsLoading({ closeDialog }: MpsLoadingProps): React.JSX.Element {
  const { data: locationList } = useLocationListQuery();
  const { mutate: createInstance } = useCreateInstanceMutation();

  // De periode waarin planners werken (X-4)
  const startDateDefault = DateTime.now().plus({ days: 28 });
  const endDateDefault = DateTime.now().plus({ days: 29 });

  const handleSubmit: React.SubmitEventHandler<TractieForm> = (event) => {
    event.preventDefault();
    const formData = event.currentTarget.serializedValue as LoadPlanForm;

    const start = combineDateAndTime(formData.startDate, formData.startTime);
    const end = combineDateAndTime(formData.endDate, formData.endTime);

    // Form input validation should already validate the date and time
    if (!start.isValid || !end.isValid) {
      console.error("Invalid start or end date/time");
      return;
    }

    createInstance({
      hubName: formData.hub,
      start: start.toUTC().toISO(),
      end: end.toUTC().toISO(),
    });
    closeDialog();
  };

  return (
    <tractie-form onSubmit={handleSubmit}>
      <form className="tractie-form-layout">
        <tractie-layout row-gap="l">
          <div className="max-w-100">
            <tractie-select label="Kies knoop" name="hub">
              <select slot="input">
                {locationList?.map((loc) => (
                  <option key={loc.code} value={loc.code}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </tractie-select>
          </div>
          <tractie-layout-slot columns={2} inset="none" className="max-w-100">
            <tractie-date-input
              label="Startdatum"
              modelValue={startDateDefault.toJSDate()}
              calendar-picker={true}
              name="startDate"
              validators={[
                new Required(undefined, { getMessage: () => "Startdatum verplicht" }),
                new IsDate(undefined, { getMessage: () => "Ongeldige datum" }),
              ]}
              formatOptions={{ locale: "nl-NL" }}
            />
            <tractie-input
              modelValue={"08:00"}
              label="Starttijd"
              type="time"
              name="startTime"
              validators={[new Required(undefined, { getMessage: () => "Starttijd verplicht" })]}
            />
          </tractie-layout-slot>
          <tractie-layout-slot columns={2} inset="none" className="max-w-100">
            <tractie-date-input
              label="Einddatum"
              modelValue={endDateDefault.toJSDate()}
              calendar-picker={true}
              name="endDate"
              validators={[
                new Required(undefined, { getMessage: () => "Einddatum verplicht" }),
                new IsDate(undefined, { getMessage: () => "Ongeldige datum" }),
              ]}
              formatOptions={{ locale: "nl-NL" }}
            />
            <tractie-input
              modelValue={"08:00"}
              label="Eindtijd"
              type="time"
              name="endTime"
              validators={[new Required(undefined, { getMessage: () => "Eindtijd verplicht" })]}
            />
          </tractie-layout-slot>

          <div className="max-w-100">
            <tractie-button-submit level="primary">Plan openen</tractie-button-submit>
          </div>
        </tractie-layout>
      </form>
    </tractie-form>
  );
}
